import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import FormDialog from "../FormDialog";
import PasswordField from "../PasswordField";
import useFormValidation from "../../../hooks/useFormValidation";
import {
  matchField,
  minLength,
  password,
  required,
} from "../../../utils/formValidation";

const ChangePasswordFormDialog = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  title = "Change Password",
  description = "",
  requireCurrentPassword = false,
}) => {
  const [formError, setFormError] = useState("");
  const schema = useMemo(
    () => ({
      ...(requireCurrentPassword
        ? { oldPassword: [required("Current password is required.")] }
        : {}),
      newPassword: [
        required("New password is required."),
        minLength(8),
        password({ min: 8 }),
      ],
      confirmPassword: [
        required("Confirm password is required."),
        matchField("newPassword", "Passwords do not match."),
      ],
    }),
    [requireCurrentPassword]
  );
  const { values, getFieldProps, validateAll, reset } = useFormValidation(
    { oldPassword: "", newPassword: "", confirmPassword: "" },
    schema
  );

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({ oldPassword: "", newPassword: "", confirmPassword: "" });
  }, [open, reset]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setFormError(
        errors.oldPassword ||
          errors.newPassword ||
          errors.confirmPassword ||
          "Please fix the password fields."
      );
      return;
    }

    setFormError("");
    try {
      if (requireCurrentPassword) {
        await onSubmit({
          newPassword: values.newPassword,
          oldPassword: values.oldPassword,
        });
      } else {
        await onSubmit(values.newPassword);
      }
    } catch (err) {
      setFormError(err?.message || "Unable to update password.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={title}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel="Update Password"
      loadingLabel="Updating..."
      maxWidth="xs"
    >
      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
      {requireCurrentPassword ? (
        <PasswordField
          label="Current Password"
          required
          autoComplete="current-password"
          {...getFieldProps("oldPassword")}
        />
      ) : null}
      <PasswordField
        label="New Password"
        required
        autoComplete="new-password"
        {...getFieldProps("newPassword")}
      />
      <PasswordField
        label="Confirm Password"
        required
        autoComplete="new-password"
        {...getFieldProps("confirmPassword")}
      />
      <Typography variant="caption" color="text.secondary">
        Min 8 characters, with uppercase, digit, and special character.
      </Typography>
    </FormDialog>
  );
};

export default ChangePasswordFormDialog;
